/**
 * From "where the phone is" to "all of the phone".
 *
 * The detector is good at the first and bad at the second: on a phone half
 * hidden by fingers it boxes the half it can see — the camera bump of a phone
 * held sideways, the bottom of one whose top runs past a knuckle. An inpainter
 * given that box sees phone at the edge of its hole and grows the phone back.
 * Measured on real selfies, a partial mask was worse than no erasure at all.
 *
 * A phone is a slab of one colour. So: take the colour at the centre of the
 * box, and flood outward through everything that still matches it, within a
 * window around the box. Where that leaks — a black phone on a black jacket
 * matches the jacket — the flood is thrown away and the box is instead
 * stretched along its long axis, which is the direction a hidden phone almost
 * always continues.
 */
import type { Box } from './geometry';

export interface SlabResult {
  /** The phone, as a mask over the window. */
  mask: Uint8Array;
  window: Box;
  /** How the mask was made. */
  method: 'flood' | 'stretch';
}

/** How far past the box the flood may reach, as a share of the box's size, per side. */
const REACH = 0.8;
/** A flood larger than this many boxes has escaped into the background. */
const LEAK = 3.2;

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted[sorted.length >> 1];
}

/** The window the flood may use, clamped to the image. */
export function slabWindow(box: Box, w: number, h: number): Box {
  const x0 = Math.max(0, Math.floor(box.x - box.w * REACH));
  const y0 = Math.max(0, Math.floor(box.y - box.h * REACH));
  const x1 = Math.min(w, Math.ceil(box.x + box.w * (1 + REACH)));
  const y1 = Math.min(h, Math.ceil(box.y + box.h * (1 + REACH)));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * The box stretched along its long axis — the fallback when the colour flood
 * cannot be trusted.
 */
export function stretchBox(box: Box, w: number, h: number): Box {
  const tall = box.h >= box.w;
  const grow = 0.35;
  const b = tall
    ? { x: box.x, y: box.y - box.h * grow, w: box.w, h: box.h * (1 + 2 * grow) }
    : { x: box.x - box.w * grow, y: box.y, w: box.w * (1 + 2 * grow), h: box.h };
  const x0 = Math.max(0, b.x);
  const y0 = Math.max(0, b.y);
  return { x: x0, y: y0, w: Math.min(w, b.x + b.w) - x0, h: Math.min(h, b.y + b.h) - y0 };
}

/**
 * @param rgba   the window's pixels, as from getImageData
 * @param window where those pixels sit in the image
 * @param box    the detector's box, in image coordinates
 */
export function growSlab(rgba: Uint8ClampedArray, window: Box, box: Box): SlabResult {
  const { w, h } = window;
  const bx0 = Math.max(0, Math.round(box.x - window.x));
  const by0 = Math.max(0, Math.round(box.y - window.y));
  const bx1 = Math.min(w, Math.round(box.x + box.w - window.x));
  const by1 = Math.min(h, Math.round(box.y + box.h - window.y));

  const boxMask = () => {
    const m = new Uint8Array(w * h);
    for (let y = by0; y < by1; y++) m.fill(1, y * w + bx0, y * w + bx1);
    return m;
  };

  // The phone's colour, from the middle of the box — the part least likely to
  // be a finger or the background.
  const cx0 = bx0 + Math.floor((bx1 - bx0) * 0.25);
  const cx1 = bx1 - Math.floor((bx1 - bx0) * 0.25);
  const cy0 = by0 + Math.floor((by1 - by0) * 0.25);
  const cy1 = by1 - Math.floor((by1 - by0) * 0.25);
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  for (let y = cy0; y < cy1; y++) {
    for (let x = cx0; x < cx1; x++) {
      const i = (y * w + x) * 4;
      rs.push(rgba[i]);
      gs.push(rgba[i + 1]);
      bs.push(rgba[i + 2]);
    }
  }
  const r0 = median(rs);
  const g0 = median(gs);
  const b0 = median(bs);
  const dist = (i: number) => Math.abs(rgba[i * 4] - r0) + Math.abs(rgba[i * 4 + 1] - g0) + Math.abs(rgba[i * 4 + 2] - b0);

  // Tolerance from the slab's own spread, so a glossy phone gets more room
  // than a matte one, within limits.
  const spread = median(rs.map((_, k) => Math.abs(rs[k] - r0) + Math.abs(gs[k] - g0) + Math.abs(bs[k] - b0)));
  const tolerance = Math.min(90, Math.max(42, spread * 3.5));

  const mask = new Uint8Array(w * h);
  const stack: number[] = [];
  for (let y = cy0; y < cy1; y++) {
    for (let x = cx0; x < cx1; x++) {
      const i = y * w + x;
      if (dist(i) <= tolerance) {
        mask[i] = 1;
        stack.push(i);
      }
    }
  }
  while (stack.length) {
    const p = stack.pop()!;
    const px = p % w;
    const py = (p / w) | 0;
    for (const q of [px > 0 ? p - 1 : -1, px < w - 1 ? p + 1 : -1, py > 0 ? p - w : -1, py < h - 1 ? p + w : -1]) {
      if (q < 0 || mask[q] || dist(q) > tolerance) continue;
      mask[q] = 1;
      stack.push(q);
    }
  }

  let area = 0;
  for (let i = 0; i < mask.length; i++) area += mask[i];
  const boxArea = Math.max(1, (bx1 - bx0) * (by1 - by0));

  // A flood that left the window's edge, or grew past several boxes, found the
  // jacket rather than the phone.
  let touchesEdge = false;
  for (let x = 0; x < w && !touchesEdge; x++) touchesEdge = !!(mask[x] || mask[(h - 1) * w + x]);
  for (let y = 0; y < h && !touchesEdge; y++) touchesEdge = !!(mask[y * w] || mask[y * w + w - 1]);
  if (area > boxArea * LEAK || touchesEdge) {
    const s = stretchBox(box, Infinity, Infinity);
    const m = new Uint8Array(w * h);
    const sx0 = Math.max(0, Math.round(s.x - window.x));
    const sy0 = Math.max(0, Math.round(s.y - window.y));
    const sx1 = Math.min(w, Math.round(s.x + s.w - window.x));
    const sy1 = Math.min(h, Math.round(s.y + s.h - window.y));
    for (let y = sy0; y < sy1; y++) m.fill(1, y * w + sx0, y * w + sx1);
    return { mask: m, window, method: 'stretch' };
  }

  // The box itself always counts: the lens, the logo and the fingers' shadow
  // inside it do not match the slab colour but are still to go.
  const box1 = boxMask();
  for (let i = 0; i < mask.length; i++) mask[i] = mask[i] || box1[i] ? 1 : 0;
  return { mask: closeAndFill(mask, w, h), window, method: 'flood' };
}

/** Close small gaps and fill enclosed holes, so a lens or a logo is not left as an island. */
function closeAndFill(m: Uint8Array, w: number, h: number): Uint8Array {
  const r = 2;
  const grown = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!m[y * w + x]) continue;
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -r; dx <= r; dx++) {
          const xx = x + dx;
          if (xx >= 0 && xx < w) grown[yy * w + xx] = 1;
        }
      }
    }
  }
  // Flood the outside from the border; whatever it cannot reach is enclosed.
  const outside = new Uint8Array(w * h);
  const stack: number[] = [];
  const seed = (i: number) => {
    if (!grown[i] && !outside[i]) {
      outside[i] = 1;
      stack.push(i);
    }
  };
  for (let x = 0; x < w; x++) {
    seed(x);
    seed((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    seed(y * w);
    seed(y * w + w - 1);
  }
  while (stack.length) {
    const p = stack.pop()!;
    const px = p % w;
    const py = (p / w) | 0;
    if (px > 0) seed(p - 1);
    if (px < w - 1) seed(p + 1);
    if (py > 0) seed(p - w);
    if (py < h - 1) seed(p + w);
  }
  const out = new Uint8Array(w * h);
  for (let i = 0; i < out.length; i++) out[i] = outside[i] ? 0 : 1;
  return out;
}
